'use client';

import { useState } from 'react';
import { Calculator, DollarSign, Percent, TrendingUp, TrendingDown, Clock, Calendar, BarChart2, RefreshCw } from 'lucide-react';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { roiCalculatorContent } from '../../data/roi-calculator-content';

type ROICalculation = {
  initialInvestment: number;
  finalValue: number;
  roi: number;
  annualizedRoi: number;
  profit: number;
};

export default function ROICalculator() {
  const [formData, setFormData] = useState({
    initialInvestment: '',
    finalValue: '',
    investmentPeriod: '1',
    includeFees: false,
    fees: '0',
    currency: 'USD'
  });

  const [results, setResults] = useState<ROICalculation | null>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const calculateROI = () => {
    const initialInvestment = parseFloat(formData.initialInvestment) || 0;
    const finalValue = parseFloat(formData.finalValue) || 0;
    const investmentPeriod = parseFloat(formData.investmentPeriod) || 1;
    const fees = formData.includeFees ? parseFloat(formData.fees) || 0 : 0;

    const profit = finalValue - initialInvestment - fees;
    const roi = (profit / initialInvestment) * 100;
    const annualizedRoi = (Math.pow(1 + (roi / 100), 1 / investmentPeriod) - 1) * 100;

    setResults({
      initialInvestment,
      finalValue,
      roi,
      annualizedRoi,
      profit
    });
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target as HTMLInputElement;

    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value
    }));
  };

  const resetForm = () => {
    setFormData({
      initialInvestment: '',
      finalValue: '',
      investmentPeriod: '1',
      includeFees: false,
      fees: '0',
      currency: 'USD'
    });
    setResults(null);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: formData.currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const formatPercentage = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'percent',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(value / 100);
  };

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <ToolPageIntro title="ROI Calculator - Free Online Return on Investment Calculator">
                Calculate ROI, profit margins, and investment returns with our free online ROI calculator. Make informed financial decisions with detailed analysis.
            </ToolPageIntro>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6 mb-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label htmlFor="initialInvestment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Initial Investment ({formData.currency})
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="initialInvestment"
                  id="initialInvestment"
                  value={formData.initialInvestment}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>

            <div>
              <label htmlFor="finalValue" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Final Value ({formData.currency})
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <DollarSign className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="finalValue"
                  id="finalValue"
                  value={formData.finalValue}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="0.00"
                  step="0.01"
                />
              </div>
            </div>

            <div>
              <label htmlFor="investmentPeriod" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Investment Period (years)
              </label>
              <div className="relative rounded-md shadow-sm">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Clock className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  type="number"
                  name="investmentPeriod"
                  id="investmentPeriod"
                  min="0.1"
                  step="0.1"
                  value={formData.investmentPeriod}
                  onChange={handleInputChange}
                  className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                />
              </div>
            </div>

            <div>
              <label htmlFor="currency" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Currency
              </label>
              <select
                id="currency"
                name="currency"
                value={formData.currency}
                onChange={handleInputChange}
                className="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm rounded-md dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="JPY">JPY (¥)</option>
                <option value="INR">INR (₹)</option>
              </select>
            </div>
          </div>

          <div className="mt-6">
            <button
              type="button"
              onClick={() => setShowAdvanced(!showAdvanced)}
              className="text-sm text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300 flex items-center"
            >
              {showAdvanced ? 'Hide' : 'Show'} Advanced Options
              {showAdvanced ? (
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              ) : (
                <svg className="ml-1 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                </svg>
              )}
            </button>

            {showAdvanced && (
              <div className="mt-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-md">
                <div className="flex items-center">
                  <input
                    id="includeFees"
                    name="includeFees"
                    type="checkbox"
                    checked={formData.includeFees}
                    onChange={handleInputChange}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded dark:bg-gray-600 dark:border-gray-500"
                  />
                  <label htmlFor="includeFees" className="ml-2 block text-sm text-gray-700 dark:text-gray-300">
                    Include Fees & Expenses
                  </label>
                </div>

                {formData.includeFees && (
                  <div className="mt-4">
                    <label htmlFor="fees" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Total Fees & Expenses ({formData.currency})
                    </label>
                    <div className="relative rounded-md shadow-sm">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <DollarSign className="h-5 w-5 text-gray-400" />
                      </div>
                      <input
                        type="number"
                        name="fees"
                        id="fees"
                        value={formData.fees}
                        onChange={handleInputChange}
                        className="focus:ring-blue-500 focus:border-blue-500 block w-full pl-10 pr-12 sm:text-sm border-gray-300 rounded-md dark:bg-gray-600 dark:border-gray-500 dark:text-white"
                        placeholder="0.00"
                        step="0.01"
                      />
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={calculateROI}
              className="inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-1"
            >
              <Calculator className="mr-2 h-4 w-4" />
              Calculate ROI
            </button>
            <button
              type="button"
              onClick={resetForm}
              className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 flex-1"
            >
              <RefreshCw className="mr-2 h-4 w-4" />
              Reset
            </button>
          </div>
        </div>

        {results && (
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4">
              <TrendingUp className="inline-block mr-2 text-green-500" />
              Investment Results
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Initial Investment</div>
                <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(results.initialInvestment)}
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-md">
                <div className="text-sm font-medium text-gray-500 dark:text-gray-400">Final Value</div>
                <div className="mt-1 text-lg font-semibold text-gray-900 dark:text-white">
                  {formatCurrency(results.finalValue)}
                </div>
              </div>

              <div className="bg-blue-50 dark:bg-blue-900/30 p-4 rounded-md">
                <div className="text-sm font-medium text-blue-700 dark:text-blue-300">Total Profit/Loss</div>
                <div className={`mt-1 text-lg font-semibold ${results.profit >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatCurrency(results.profit)}
                  {results.profit >= 0 ? (
                    <TrendingUp className="inline-block ml-2 h-5 w-5" />
                  ) : (
                    <TrendingDown className="inline-block ml-2 h-5 w-5" />
                  )}
                </div>
              </div>

              <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-md">
                <div className="text-sm font-medium text-green-700 dark:text-green-300">ROI</div>
                <div className={`mt-1 text-lg font-semibold ${results.roi >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'}`}>
                  {formatPercentage(results.roi)}
                  {results.roi >= 0 ? (
                    <TrendingUp className="inline-block ml-2 h-5 w-5" />
                  ) : (
                    <TrendingDown className="inline-block ml-2 h-5 w-5" />
                  )}
                </div>
              </div>

              <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-md">
                <div className="text-sm font-medium text-purple-700 dark:text-purple-300">Annualized ROI</div>
                <div className="mt-1 text-lg font-semibold text-purple-600 dark:text-purple-400">
                  {formatPercentage(results.annualizedRoi)}
                </div>
              </div>

              <div className="bg-yellow-50 dark:bg-yellow-900/20 p-4 rounded-md">
                <div className="text-sm font-medium text-yellow-700 dark:text-yellow-300">Investment Period</div>
                <div className="mt-1 text-lg font-semibold text-yellow-600 dark:text-yellow-400">
                  {formData.investmentPeriod} {parseFloat(formData.investmentPeriod) === 1 ? 'Year' : 'Years'}
                </div>
              </div>
            </div>

            <div className="mt-6 p-4 bg-blue-50 dark:bg-blue-900/10 rounded-md border border-blue-100 dark:border-blue-900/30">
              <h3 className="text-sm font-medium text-blue-800 dark:text-blue-200">
                <svg className="inline-block mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                About ROI
              </h3>
              <p className="mt-1 text-sm text-blue-700 dark:text-blue-300">
                ROI (Return on Investment) measures the gain or loss generated on an investment relative to the amount of money invested.
                The annualized ROI shows the average yearly return, which is useful for comparing investments of different time periods.
              </p>
            </div>
          </div>
        )}

        {/* Informational Content */}
        <div className="mt-12">
          <InformationalContent
            title={roiCalculatorContent.title}
            description={roiCalculatorContent.description}
            content={roiCalculatorContent.content}
            faqs={roiCalculatorContent.faqs}
            relatedTools={roiCalculatorContent.relatedTools}
          />
        </div>
      </div>
    </div>
  );
}
